import React, { useState } from 'react';
import { Leaf, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/wildlife', label: t('nav.wildlife') },
    { to: '/trekking', label: t('nav.trekking') },
    { to: '/stories', label: t('nav.stories') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const linkStyle = (path) => ({
    color: isActive(path) ? '#ea580c' : '#555',
    fontWeight: isActive(path) ? 700 : 500,
    fontSize: '0.95rem',
    textDecoration: 'none',
    borderBottom: isActive(path) ? '2px solid #ea580c' : 'none',
    paddingBottom: '4px',
  });

  return (
    <>
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 2rem',
        backgroundColor: '#fffefdff',
        boxShadow: '0 2px 10px rgba(234,88,12,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', textDecoration: 'none' }}>
          <div style={{
            backgroundColor: '#ea580c',
            color: 'white',
            padding: '6px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Leaf size={20} />
          </div>
          <span style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            fontFamily: 'var(--font-sans)',
            color: '#111'
          }}>
            Adventure
          </span>
        </Link>

        {/* Desktop Navigation Links */}
        {localStorage.getItem('is_admin') !== 'true' && (
          <div className="desktop-nav" style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            {navLinks.map(link => (
              <Link key={link.to} to={link.to} style={linkStyle(link.to)}>{link.label}</Link>
            ))}
          </div>
        )}

        {/* Desktop Action Area */}
        <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {location.pathname !== '/admin' && <LanguageSwitcher />}
          {localStorage.getItem('token') ? (
            <>
              {localStorage.getItem('is_admin') === 'true' ? (
                <Link to="/admin" style={{ color: '#ea580c', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}>{t('nav.admin')}</Link>
              ) : null}
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('is_admin');
                  window.location.href = '/';
                }}
                style={{ color: '#991b1b', fontWeight: 600, fontSize: '1rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <Link to="/login" style={{ color: '#555', fontWeight: 600, fontSize: '1rem', textDecoration: 'none' }}>{t('nav.login')}</Link>
          )}
        </div>

        {/* Hamburger Button — mobile only */}
        <button
          className="hamburger-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#111'
          }}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </nav>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'fixed',
              top: '72px',
              left: 0,
              right: 0,
              backgroundColor: '#fffefdff',
              boxShadow: '0 8px 20px rgba(234,88,12,0.15)',
              zIndex: 999,
              padding: '1.5rem 2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
              borderBottom: '2px solid #f6c87a'
            }}
          >
            {localStorage.getItem('is_admin') !== 'true' && navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMenuOpen(false)}
                style={{
                  ...linkStyle(link.to),
                  fontSize: '1.1rem',
                  paddingBottom: '0.5rem',
                  borderBottom: isActive(link.to) ? '2px solid #ea580c' : '1px solid #f0e6d3',
                }}
              >
                {link.label}
              </Link>
            ))}

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', paddingTop: '0.5rem', flexWrap: 'wrap' }}>
              {location.pathname !== '/admin' && <LanguageSwitcher />}
              {localStorage.getItem('token') ? (
                <>
                  {localStorage.getItem('is_admin') === 'true' && (
                    <Link to="/admin" onClick={() => setMenuOpen(false)} style={{ color: '#ea580c', fontWeight: 700 }}>{t('nav.admin')}</Link>
                  )}
                  <button
                    onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('is_admin'); window.location.href = '/'; }}
                    style={{ color: '#991b1b', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '1rem' }}
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMenuOpen(false)} style={{ color: '#555', fontWeight: 600 }}>{t('nav.login')}</Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger-btn { display: block !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;
